import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, NavLink, useLocation, useParams, Redirect, Switch } from 'react-router-dom';
import { DateTime } from 'luxon';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';

import Entries from 'app/components/form/Entries';
import FormSettings from 'app/components/form/FormSettings';
import FormBuilderSettings from 'app/components/form/FormBuilderSettings';
import EditEntry from 'app/components/form/EditEntry';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { container} from 'app/styles/new-layout'
import { paper } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        minHeight: '80vh',
        position: 'relative'
    },
    paper: {
        ...paper(theme)
    },
    header: {
        padding: theme.spacing(3)
    }
}));

export default function EditForm() {

    let location = useLocation();
    let params = useParams();
    const api = useApi();

    const [ form, setForm ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const [ groups, setGroups ] = useState(null);
    const [ urlToCopyText, setUrlToCopyText ] = useState("Copy to Clipboard");

    const classes = useStyles();
    const formID = params.id

    const pathname = location.pathname;

    // Getters ------------------------------------------

    const getForm = ($quiet = false) => {

        if (! $quiet) setLoading(true);

        api.get(`/admin/form/${formID}`)
            .then(res => {
                setForm({
                    ...res.form,
                    created_at: DateTime.fromISO(res.form.created_at),
                    starts_at: (res.form.starts_at !== null) ? DateTime.fromISO(res.form.starts_at) : null,
                    ends_at: (res.form.ends_at !== null) ? DateTime.fromISO(res.form.ends_at) : null,
                });
            }).finally(() => setLoading(false))
    }

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));

    // Effects ------------------------------------------

    useEffect(() => {
        getForm();
        getGroups();
    }, []);

    // Constants ---------------------------------------------------

    const isSelected = tab => (pathname.includes(tab));

    const copyToClipboard = () => {
        const range = document.createRange();
        range.selectNode(document.getElementById("url-to-copy"));
        window.getSelection().removeAllRanges(); // clear current selection
        window.getSelection().addRange(range); // to select text
        document.execCommand("copy");
        window.getSelection().removeAllRanges();// to deselect
        setUrlToCopyText('Copied!');
    }

    return (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            {(form && groups) &&
                <Paper>
                    <div className={classes.header}>
                        <Grid container spacing={3} justify="space-between" alignItems="center">
                            <Grid item>
                                <Typography color="secondary" variant="h4">
                                    {form.name}
                                </Typography>
                                {(form.global) && 
                                    <Typography variant="subtitle">
                                        Public Link:&nbsp;&nbsp;&nbsp;
                                        <Tooltip title={urlToCopyText}>
                                            <Link onClick={copyToClipboard} href="#">
                                                <span id="url-to-copy">{form.global_url}</span>
                                            </Link>
                                        </Tooltip>
                                    </Typography>
                                }
                            </Grid>
                            <Grid item>
                                <ButtonGroup color="secondary" aria-label="form navigation">
                                    <Button
                                        to={`/admin/forms/${formID}/entries`}
                                        variant={isSelected(`/admin/forms/${formID}/entries`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Entries
                                    </Button>
                                    <Button
                                        to={`/admin/forms/${formID}/settings`}
                                        variant={isSelected(`/admin/forms/${formID}/settings`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Form Settings
                                    </Button>
                                    <Button
                                        to={`/admin/forms/${formID}/builder`}
                                        variant={isSelected(`/admin/forms/${formID}/builder`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Form Builder
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                        </Grid>
                    </div>
                    <Divider />
                    <Switch>
                        <Route path={`/admin/forms/${formID}/entries`} exact>
                            <Entries
                                form={form}
                                formID={formID}
                                getForm={getForm}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/forms/${formID}/proposals/:id`} exact>
                            <EditEntry
                                form={form}
                                getForm={getForm}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/forms/${formID}/settings`}>
                            <FormSettings
                                form={form}
                                groups={groups}
                                getForm={getForm}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/forms/${formID}/builder`}>
                            <FormBuilderSettings
                                form={form}
                                getForm={getForm}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Redirect from={`/admin/forms/${formID}`} to={`/admin/forms/${formID}/entries`}/>
                        <Redirect from={`/admin/forms/${formID}/*`} to={`/admin/forms/${formID}/entries`}/>
                    </Switch>
                </Paper>
            }
        </div>
    );
}
